import { useEffect, useRef, useState } from 'react'
import { CATEGORY_SETUP, useEditor, type LightCategory } from '../store/useEditor'
import { SuggestBlock } from './SuggestBlock'
import {
  IconBow,
  IconBush,
  IconCluster,
  IconColumn,
  IconMoon,
  IconPlus,
  IconRoofline,
  IconSpark,
  IconSun,
  IconSwag,
  IconTree,
  IconWreath,
} from './icons'

/**
 * THE WHOLE INTERFACE, ON ONE BAR.
 *
 * The panel this replaces was a rail down the side of the photograph with a
 * three step header, fourteen sections and about thirty controls. It made the
 * photo the leftover space, and a contractor standing in a driveway with a
 * customer waiting does not read a settings panel.
 *
 * So the photo fills the screen and there are THREE things to press. Smart
 * roof asks the model to find the roof lines. The middle button NAMES what you
 * are drawing - press it to switch, or to open how it looks. Night turns the
 * picture to evening, and hovering it hands over the amount.
 *
 * Export left this bar for the top one. Everything down here is something you
 * do TO the photograph while you work; export is the last thing you do, once,
 * and it edits nothing. Keeping it here made the bar four wide for the sake of
 * a button pressed at the end of the job.
 *
 * Every one of the three opens SMALL and goes deeper only if asked. Reported
 * as: the controls are so big and seem like too much. Everything else is a
 * property of something you have selected, and it lives in the inspector,
 * which only exists while something is selected.
 *
 * V1 IS A VISUAL TOOL, NOT A QUOTING ONE. There is no scale step, no footage
 * and no money anywhere on this bar or in anything it opens. Export means send
 * the customer the picture.
 */

/**
 * THE NINE THINGS A CONTRACTOR LIGHTS, in the order they get asked for.
 *
 * COLUMNS WAS MISSING FROM THIS LIST and nowhere else. The category exists,
 * draws, prices and saves; it simply had no row here, so the only way to light
 * a column was to turn on Advanced and find it in the workshop rail. A
 * category reachable in one mode and invisible in the other is not a smaller
 * interface, it is a missing feature that looks like a design decision.
 *
 * Accessories also wore the wreath's icon, which put the same picture on two
 * different rows: two things that look identical in a picker are two things
 * somebody presses the wrong one of.
 */
const CATS: { id: LightCategory; icon: React.ReactNode }[] = [
  { id: 'roofline', icon: <IconRoofline /> },
  { id: 'garland', icon: <IconSwag /> },
  { id: 'columns', icon: <IconColumn /> },
  { id: 'palms', icon: <IconTree /> },
  { id: 'trees', icon: <IconTree /> },
  { id: 'other', icon: <IconBush /> },
  { id: 'wreaths', icon: <IconWreath /> },
  { id: 'clusters', icon: <IconCluster /> },
  { id: 'accessories', icon: <IconBow /> },
]

/**
 * Where Night lands when you press it.
 *
 * Not the maximum. The homeowner has to recognize their own house - the trim,
 * the garage, the planting - with the lights plainly the brightest thing on it.
 * Carried over from the top bar control this replaces.
 */
const DEFAULT_NIGHT = 0.75

type Sheet = 'add' | 'smart' | 'night' | 'export' | null

export default function Dock({
  onExport,
  exporting,
  onShare,
  canShare,
}: {
  onExport: () => void
  exporting: boolean
  onShare: () => void
  canShare: boolean
}) {
  const [sheet, setSheet] = useState<Sheet>(null)
  /*
   * THE SLIDER APPEARS UNDER THE POINTER, not behind a press.
   *
   * Once it is evening, how dark it is becomes the one thing anybody touches,
   * and almost nobody goes back to daylight. So hovering the Night button
   * hands over the slider directly. The button itself keeps meaning what it
   * says - press it and you are back to the photo as shot - and the chevron
   * still opens the sheet, which is the only route a finger has, because a
   * touch screen has no hover to reveal anything with.
   */
  const [dim, setDim] = useState(false)
  const wrapRef = useRef<HTMLDivElement | null>(null)

  const doc = useEditor((s) => s.doc)
  const present = useEditor((s) => s.present)
  const category = useEditor((s) => s.category)
  const setCategory = useEditor((s) => s.setCategory)
  const tool = useEditor((s) => s.tool)
  const setTool = useEditor((s) => s.setTool)
  const setScene = useEditor((s) => s.setScene)
  const setPresent = useEditor((s) => s.setPresent)
  const selectLayer = useEditor((s) => s.selectLayer)
  const selectProposal = useEditor((s) => s.selectProposal)
  const suggest = useEditor((s) => s.suggest)
  const accessCode = useEditor((s) => s.accessCode)
  const generate = useEditor((s) => s.generateSuggestions)

  const nightOn = doc.scene.night > 0.01
  const running = suggest.phase === 'uploading' || suggest.phase === 'analyzing'
  const hasPhoto = !!doc.photo

  // A sheet is a transient thing. Clicking the photo, or pressing Escape,
  // should put it away rather than leaving it parked over the house.
  useEffect(() => {
    if (!sheet) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setSheet(null)
    }
    const onDown = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setSheet(null)
    }
    window.addEventListener('keydown', onKey)
    document.addEventListener('mousedown', onDown)
    return () => {
      window.removeEventListener('keydown', onKey)
      document.removeEventListener('mousedown', onDown)
    }
  }, [sheet])

  /*
   * NO PHOTO, NO BAR.
   *
   * Three of the four do nothing without a picture and the fourth arms a tool
   * with nothing to draw on, so the bar was four dead controls sitting on top
   * of the upload screen - and on a phone it covered the sample-house link
   * outright, which is how this was found. The drop-the-photo screen gets the
   * whole window; the bar arrives with the house.
   */
  if (present || !hasPhoto) return null

  const open = (s: Sheet) => setSheet((v) => (v === s ? null : s))

  /*
   * SMART ROOF, AND THE THING THAT MUST NOT CHANGE.
   *
   * Every press here is a paid reading of a photograph, and whatever comes
   * back arrives as a SUGGESTION that a person still has to include. That
   * logic lives in SuggestBlock and is not duplicated: this button either
   * fires the one uncomplicated case, or opens the sheet that owns the rest -
   * the access code, the progress and cancel, and the confirmation before a
   * second generation.
   */
  /**
   * THE AI ROOF IS OFF, AND IT IS OFF IN ONE PLACE.
   *
   * Not deleted. Every piece of it - the endpoint, the gate, the fail-closed
   * adapter, the suggestion layer that keeps a guess out of the priced
   * collection until somebody says yes - is still here and still tested. What
   * is switched off is the button.
   *
   * Two reasons, and the second is the real one. The reading is billed per
   * photograph today, and a detector trained on this trade's own photos is in
   * progress that will run in the browser for nothing. Shipping a paid button
   * that is about to be replaced teaches people a price that is not going to
   * be the price.
   *
   * It stays on screen, grayed, saying what it will be. A feature that
   * vanishes leaves somebody wondering whether they imagined it; a feature
   * that says Coming soon is a promise with a date attached to it somewhere.
   *
   * Turning it back on is this one line.
   */
  const AI_READY = false

  const smartLabel = running
    ? suggest.phase === 'uploading'
      ? 'Sending'
      : 'Reading'
    : suggest.generation
      ? 'Roof again'
      : 'AI roof'

  const onSmart = () => {
    /*
     * IT JUST RUNS.
     *
     * A second press used to open a sheet asking permission to spend another
     * reading, explaining what would be replaced and what would survive.
     * Reported as: what are all these screens that come up for AI, it should
     * just do its thing, I do not want all these complicated things that
     * confuse people.
     *
     * The confirmation was written when a call was one of ONE a day and a
     * regeneration silently threw away suggestions. Neither is true now. The
     * spend is bounded by the daily ceilings, a regeneration spares every run
     * you drew or moved, and one undo puts the whole generation back. Asking
     * permission for something bounded and reversible is not caution, it is a
     * door in the middle of a corridor.
     *
     * Two things still open the sheet, and neither is a question: a run in
     * flight, because that is where Cancel lives, and a missing sign in,
     * because there is nothing to press until it is fixed.
     */
    if (running || !accessCode) {
      open('smart')
      return
    }
    void generate()
  }

  return (
    <div className="dock-wrap" ref={wrapRef}>
      {/* ---------------- sheets ---------------- */}

      {sheet === 'add' && (
        /*
          A LIST OF WORDS, AND NOTHING ELSE IN IT.

          This picker held the color and spacing controls for a while, which
          made it a slab floating over the middle of the house - and a panel
          that covers the thing you are looking at is the one thing every
          canvas tool has learned not to do. Reported as: something is off,
          especially when you have to hunt for what you are looking for.

          Settings moved to the panel on the right, where they have a fixed
          home and never cover the photograph. What is left here is the choice
          itself: one row of words, the armed one lit, exactly the list that
          was here before, at a tenth of the height.
        */
        <div className="dock-sheet narrow">
          {/*
            THE SAME TILE AS THE BAR, JUST MORE OF THEM.

            These were pills - fully rounded capsules sized to their own text -
            which read as a row of tags next to a bar made of square bevelled
            tiles, and the longer labels collided with each other. Reported as:
            I do not like these buttons, it needs to follow the same style as
            the menu at the bottom, just an expanded version, same boxes same
            size.

            So it is literally the class the bar uses. The grid makes every cell
            the same size and lets a two word label wrap instead of running into
            its neighbor, which is the one thing the bar itself never has to
            handle.
          */}
          <div className="dock-picker">
            {CATS.map((c) => (
              <button
                key={c.id}
                className="dock-btn"
                aria-pressed={category === c.id && tool !== 'select'}
                onClick={() => {
                  /*
                   * PRESS THE ARMED ONE AGAIN AND THE TOOL GOES AWAY.
                   *
                   * Reported as: sometimes I click on the picture and it
                   * automatically starts dragging a roof line, maybe because
                   * I am just clicking around. The tool stays armed on purpose
                   * so that six runs on a house are not six trips to a menu,
                   * and the cost of that is exactly this: there was no way to
                   * put it down.
                   *
                   * A press on the lit tile disarms. Escape does too. That is
                   * the neutral state every canvas tool has, and it is a
                   * better answer than making somebody hold still for a beat
                   * before every line - which would tax the hundreds of
                   * deliberate presses to forgive the occasional stray one.
                   */
                  if (category === c.id && tool !== 'select') {
                    setTool('select')
                  } else {
                    setCategory(c.id)
                  }
                  selectLayer(null)
                  selectProposal(null)
                  /*
                   * AND THE PICKER GOES AWAY.
                   *
                   * It stayed open while it also held the color and spacing
                   * controls, which were a second reason to be looking at it.
                   * Those live in the panel now, so the only thing left in
                   * here is the choice - and once the choice is made the bar
                   * below already says what it was. Reported as: after you
                   * click it should disappear, it is already selected at the
                   * bottom, it should not stay up.
                   */
                  setSheet(null)
                }}
              >
                <span className="dt-icon">{c.icon}</span>
                <span>{CATEGORY_SETUP[c.id].label}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {sheet === 'smart' && (
        <div className="dock-sheet narrow">
          <SuggestBlock />
        </div>
      )}

      {sheet === 'night' && (
        <div className="dock-sheet narrow">
          <h2>How dark</h2>
          <div className="dock-night">
            <input
              type="range"
              min={0}
              max={1}
              step={0.01}
              value={doc.scene.night}
              disabled={!hasPhoto}
              onChange={(e) => setScene({ night: parseFloat(e.target.value) })}
              aria-label="Night intensity"
            />
            <b>{Math.round(doc.scene.night * 100)}%</b>
          </div>
          <p className="dock-hint">
            The evening is graded from your own photograph, so there is no one right amount of it.
            This is the only adjustment anybody makes.
          </p>
        </div>
      )}

      {/* ---------------- the bar ---------------- */}

      <div className="dock">
        <button
          /*
            NOT READY IS NOT PRIMARY.

            AI roof was wearing the primary tile, so the loudest control on the
            bar was the one thing on it that does nothing. It takes the plain
            tile until the feature is switched on, and the Soon badge moves to
            a corner chip rather than a third row of its own - which is why
            this tile stood taller than the other two and made the bar ragged.
          */
          className={AI_READY ? 'dock-btn primary' : 'dock-btn dock-soon-btn'}
          onClick={AI_READY ? onSmart : undefined}
          disabled={!AI_READY || !hasPhoto}
          aria-busy={running}
          title={
            AI_READY
              ? 'Let the AI find the roof lines in this photo'
              : 'Automatic roof detection is not switched on yet'
          }
        >
          <IconSpark size={18} />
          <span>{AI_READY ? smartLabel : 'AI roof'}</span>
          {!AI_READY && <em className="dock-soon">Soon</em>}
        </button>

        <span className="dock-sep" />

        {/*
          WHAT YOU ARE DRAWING, WRITTEN ON THE BUTTON.

          It said Add, which is a verb with no object: nothing on screen told
          you a roofline was armed, so a press on the photo was always a small
          surprise. It now names the category, which makes the same button the
          answer to two questions at once - what am I working with, and how do
          I change it. Asked for as: whenever you are working with something it
          should be clear we are working currently with roofline, and we need a
          way to switch items.
        */}
        <button
          className="dock-btn has-more"
          aria-pressed={sheet === 'add'}
          onClick={() => open('add')}
          title="What you are drawing. Press to switch, or to change how it looks."
        >
          {CATS.find((c) => c.id === category)?.icon ?? <IconPlus size={18} />}
          <span>{tool === 'select' ? 'Add' : CATEGORY_SETUP[category].label}</span>
          {/* The frames draw an up-down pair, not a down chevron: this cycles
              through a set rather than dropping a menu below itself. */}
          <span className="dock-more" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
              <path d="m7 15 5 5 5-5M7 9l5-5 5 5" />
            </svg>
          </span>
        </button>

        {/*
          THE HOVER TARGET IS THE WRAPPER, AND IT TOUCHES THE SLIDER.

          The popover used to live INSIDE the button, eight pixels above it,
          with the button owning the hover. Crossing those eight pixels put the
          pointer over neither element, which is a mouseleave, which closed the
          thing you were reaching for. Reported as: it does not let me get to
          the slider, it keeps going out when I go to it.

          Two fixes and both are needed. The hover now belongs to a wrapper
          that contains BOTH the button and the popover, so travelling between
          them never leaves it. And the gap is padding on the popover rather
          than empty space, so the hoverable area is continuous - there is no
          longer a strip of nothing to cross.

          A range input is also no longer a child of a button, which is invalid
          markup and is why dragging the slider behaved oddly at the edges.
        */}
        <span
          style={{ position: 'relative', display: 'inline-flex' }}
          onMouseEnter={() => setDim(true)}
          onMouseLeave={() => setDim(false)}
        >
        <button
          className="dock-btn has-more"
          aria-pressed={nightOn}
          disabled={!hasPhoto}
          onClick={() => setScene({ night: nightOn ? 0 : DEFAULT_NIGHT })}
          title={nightOn ? 'Back to the photo as shot' : 'Make it evening'}
        >
          {nightOn ? <IconSun size={18} /> : <IconMoon size={18} />}
          <span>{nightOn ? 'Daylight' : 'Night'}</span>
          {/*
            The amount of evening is a second question and it gets a second
            target, not a second button: press the word for on and off, press
            the corner for how much. Its own element so a thumb aiming at Night
            never lands on the slider by accident.
          */}
          <span
            className="dock-more"
            role="button"
            tabIndex={0}
            aria-label="How dark"
            onClick={(e) => {
              e.stopPropagation()
              open('night')
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                e.stopPropagation()
                open('night')
              }
            }}
          >
            <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
              <path d="m7 15 5 5 5-5M7 9l5-5 5 5" />
            </svg>
          </span>
        </button>
          {nightOn && dim && (
            <span
              onClick={(e) => e.stopPropagation()}
              onPointerDown={(e) => e.stopPropagation()}
              style={{
                position: 'absolute',
                bottom: '100%',
                left: '50%',
                transform: 'translateX(-50%)',
                // The gap is padding, so the hover area has no hole in it.
                paddingBottom: 8,
                cursor: 'default',
              }}
            >
              <span
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '8px 11px',
                  borderRadius: 10,
                  /* The chrome is light now, and a black chip floating over a
                     light dock is the one thing on screen wearing last year's
                     paint. Tokens, so it follows whatever the shell is. */
                  background: 'var(--surface-1)',
                  border: '1px solid var(--border-hover)',
                  boxShadow: '0 1px 2px rgba(23,32,30,0.10), 0 12px 30px rgba(23,32,30,0.20)',
                  color: 'var(--text-primary)',
                }}
              >
                <input
                  type="range"
                  min={0.05}
                  max={1}
                  step={0.01}
                  value={doc.scene.night}
                  onChange={(e) => setScene({ night: parseFloat(e.target.value) })}
                  aria-label="How dark"
                  style={{ width: 116 }}
                />
                <b
                  style={{
                    fontSize: 12,
                    fontVariantNumeric: 'tabular-nums',
                    minWidth: 30,
                    textAlign: 'right',
                  }}
                >
                  {Math.round(doc.scene.night * 100)}%
                </b>
              </span>
            </span>
          )}
        </span>

      </div>

      {/*
        NO HINT LINE. It said Select an item on the photo to edit it, sitting
        over the bottom of the house in gray, on every photograph, forever -
        including on a photograph with nothing drawn on it yet, where there is
        no item to select. A sentence that is wrong on the first screen and
        redundant on every screen after it is not instruction, it is a smudge
        on the picture. The same words were on the context bar too, which is
        also gone.
      */}
    </div>
  )
}
